import React, { useCallback, useEffect, useState } from 'react';
import {
  AutoComplete,
  AutoCompleteItem,
  DropdownItem,
  useTranslation,
} from '@testsigma/atoms';

import classNames from 'classnames';

import ApplicationService from '@services/application.service';
import ProjectService from '@services/project.service';
import VersionService from '@services/version.service';

import { ApplicationType } from '@testsigma/organism';
import { ApplicationVersion } from '@models/application-version.model';
import { Application } from '@models/application.model';
import { Page } from '@models/page';
import { Project } from '@models/project.model';
import { logErrorToSentry } from '@utils/common';

type Variant = 'postman-import' | 'runtime-test-data' | 'e2e-suites' | 'export';

interface ProjectProps {
  applicationVersionId: number;
  onChangeApplicationVerisonId: (event) => void;
  variant?: Variant;
  projectAutoCompleteClassName?: string;
}

interface ApplicationAutoCompleteItem {
  id: string | number;
  label: string;
  disabled: boolean;
  applicationType?: ApplicationType;
}

const applicationIconsMap = {
  WebApplication: 'web',
  MobileWeb: 'real-mobile',
  AndroidNative: 'android',
  IOSNative: 'ios',
  Rest: 'rest-api',
  WINDOWS: 'windows',
  Salesforce: 'salesforce',
};

export const ProjectSelection: React.FC<ProjectProps> = ({
  applicationVersionId,
  onChangeApplicationVerisonId,
  variant = 'postman-import',
  projectAutoCompleteClassName = '',
}) => {
  const { t } = useTranslation();
  const [selectedProject, setSelectedProject] = useState<AutoCompleteItem>(
    {} as AutoCompleteItem,
  );
  const [selectedApplication, setSelectedApplication] =
    useState<ApplicationAutoCompleteItem>({} as ApplicationAutoCompleteItem);
  const [selectedVersion, setSelectedVersion] = useState<AutoCompleteItem>(
    {} as AutoCompleteItem,
  );

  // hook to fetch projects list
  const projectOptionsHook = useCallback(
    (query: string) =>
      new Promise<AutoCompleteItem[]>((resolve) => {
        query = query ? `name:*${query}*` : '';
        ProjectService.findAll(query)
          .then((res: Page<Project>) => {
            const options = res.content.map((project) => {
              return {
                id: project.id,
                label: project.name as string,
                disabled: false,
              };
            });
            resolve(options);
          })
          .catch((err) => {
            logErrorToSentry({ error: err });
          });
      }),
    [],
  );

  // hook to fetch applications list based on selected project
  const applicationOptionsHook = useCallback(
    (term: string) =>
      new Promise<AutoCompleteItem[]>((resolve) => {
        const termQuery = `,name:*${term}*`;
        const query = `projectId:${selectedProject.id}${term ? termQuery : ''}`;
        ApplicationService.findAll(query)
          .then((res: Page<Application>) => {
            const options = res.content.map((application) => {
              return {
                id: application.id,
                label: application.name as string,
                disabled: false,
                applicationType: application.applicationType,
              };
            });
            resolve(options);
          })
          .catch((err) => {
            logErrorToSentry({ error: err });
          });
      }),
    [selectedProject],
  );

  // hook to fetch versions list based on selected applications
  const versionOptionsHook = useCallback(
    (term: string) =>
      new Promise<AutoCompleteItem[]>((resolve) => {
        const termQuery = `,versionName:*${term}*`;
        const query = `applicationId:${selectedApplication.id}${
          term ? termQuery : ''
        }`;
        VersionService.findAll(query)
          .then((res: Page<ApplicationVersion>) => {
            const options = res.content.map((version) => {
              return {
                id: version.id,
                label: version.name as string,
                disabled: false,
              };
            });
            resolve(options);
          })
          .catch((err) => {
            logErrorToSentry({ error: err });
          });
      }),
    [selectedApplication],
  );

  // sets version, application and projret on component
  const setInitialState = useCallback(
    (applicationVersionId: string | number) => {
      if (!applicationVersionId) {
        return;
      }
      VersionService.show(applicationVersionId as number)
        .then((res: ApplicationVersion) => {
          setSelectedVersion({
            id: res.id,
            label: res.name as string,
            disabled: false,
          });
          setSelectedApplication({
            id: res.application.id,
            label: res.application.name as string,
            disabled: false,
            applicationType: res.application.applicationType,
          });
          setSelectedProject({
            id: res.application.project.id,
            label: res.application.project.name as string,
            disabled: false,
          });
        })
        .catch((err) => {
          logErrorToSentry({ error: err });
        });
    },
    [selectedVersion],
  );

  const onChangeProject = useCallback(
    (project: AutoCompleteItem) => {
      setSelectedProject(project);
      setDefaultApplication(project.id);
    },
    [selectedProject],
  );

  const onChangeApplication = useCallback(
    (application: ApplicationAutoCompleteItem) => {
      setSelectedApplication(application);
      setDefaultVersion(application.id);
    },
    [selectedApplication],
  );

  const onChangeVersion = useCallback(
    (version: AutoCompleteItem) => {
      setSelectedVersion(version);
      onChangeApplicationVerisonId(version.id);
    },
    [selectedVersion],
  );

  const setDefaultApplication = useCallback(
    (projectId) => {
      const query = `projectId:${projectId}`;
      ApplicationService.findAll(query)
        .then((res: Page<Application>) => {
          const application = res.content[0];
          onChangeApplication({
            id: application.id,
            label: application.name as string,
            disabled: false,
            applicationType: application.applicationType,
          });
        })
        .catch((err) => {
          logErrorToSentry({ error: err });
        });
    },
    [selectedApplication],
  );

  const setDefaultVersion = useCallback(
    (applicationId) => {
      const query = `applicationId:${applicationId}`;
      VersionService.findAll(query)
        .then((res: Page<ApplicationVersion>) => {
          const version = res.content[0];
          onChangeVersion({
            id: version.id,
            label: version.name as string,
            disabled: false,
          });
        })
        .catch((err) => {
          logErrorToSentry({ error: err });
        });
    },
    [selectedApplication],
  );

  // on load calls setInitialState
  useEffect(() => {
    setInitialState(applicationVersionId);
  }, []);

  return (
    <>
      <div className={classNames({ 'flex gap-3': variant === 'e2e-suites' })}>
        {/* select project */}
        <AutoComplete
          key={`project_${selectedProject.id}`}
          LoadingContent={
            <DropdownItem isSelected={false}>
              {t('project.loading.message')}
            </DropdownItem>
          }
          emptyContent={
            <DropdownItem isSelected={false}>
              {t('common.no-matching-options')}
            </DropdownItem>
          }
          optionsHook={projectOptionsHook}
          placeholder={t('project.label')}
          onSelect={onChangeProject}
          value={selectedProject}
          className={classNames('mt-5 !z-50', projectAutoCompleteClassName, {
            '!w-1/3': variant === 'e2e-suites',
          })}
          showClearButton={false}
          inputClassName={classNames('bg-secondary-50 !pl-7', {
            'truncate !pr-0': variant === 'e2e-suites',
          })}
          inputContainerClassName='!bg-secondary-50'
          label={t('project.label')}
          labelClassName='text-2xs'
          prefixIcon='project-case'
          showSearchOnDropdown={true}
          dropdownClass='max-h-52 !overflow-auto'
        />

        <div
          className={classNames({
            'gap-3 grid grid-cols-2': variant !== 'postman-import',
          })}>
          {/* select application */}
          {selectedProject?.id && (
            <AutoComplete
              key={`app_${selectedApplication.id}`}
              LoadingContent={
                <DropdownItem isSelected={false}>
                  {t('application.loading.message')}
                </DropdownItem>
              }
              emptyContent={
                <DropdownItem isSelected={false}>
                  {t('common.no-matching-options')}
                </DropdownItem>
              }
              optionsHook={applicationOptionsHook}
              placeholder={t('application.label')}
              onSelect={onChangeApplication}
              value={selectedApplication}
              className='mt-5 !z-20'
              showClearButton={false}
              inputClassName={classNames('bg-secondary-50 !pl-7', {
                'truncate !pr-0': variant === 'e2e-suites',
              })}
              inputContainerClassName={'!bg-secondary-50'}
              label={t('application.label')}
              labelClassName='text-2xs'
              prefixIcon={
                applicationIconsMap[selectedApplication.applicationType]
              }
              showSearchOnDropdown={true}
              dropdownClass='max-h-52 !overflow-auto'
            />
          )}

          {/* select version */}
          {selectedApplication?.id && (
            <AutoComplete
              key={`version_${selectedVersion.id}`}
              LoadingContent={
                <DropdownItem isSelected={false}>
                  {t('version.loading.message')}
                </DropdownItem>
              }
              emptyContent={
                <DropdownItem isSelected={false}>
                  {t('common.no-matching-options')}
                </DropdownItem>
              }
              optionsHook={versionOptionsHook}
              placeholder={t('version.label')}
              onSelect={onChangeVersion}
              value={selectedVersion}
              className='mt-5'
              showClearButton={false}
              inputClassName={classNames('bg-secondary-50 !pl-7', {
                'truncate !pr-0': variant === 'e2e-suites',
              })}
              inputContainerClassName={'!bg-secondary-50'}
              label={t('version.label')}
              labelClassName='text-2xs'
              prefixIcon='versions'
              showSearchOnDropdown={true}
              dropdownClass='max-h-52 !overflow-auto'
            />
          )}
        </div>
      </div>
    </>
  );
};
